import { ENDPOINTS } from "utils/endpoints";
import { GET_API_CALL, PATCH_API_CALL, POST_API_CALL } from "utils/services";
import { APIResponseModal } from "utils/modals";
import {
  UPDATE_USER_DETAIL_REDUCER,
  UPDATE_USER_REDUCER,
} from "store/actionTypes";
import SHOW_TOAST from "utils/showToast";
import {
  buildUserPayloadByRole,
  mapPaginatedResult,
  mapUserDetail,
  mapUserListItem,
} from "utils/apiMappers";

/**
 * GET /api/v1/admin/user-lists?page&limit&search&type
 * `search` matches firstName / lastName / phone / email on the backend.
 */
export const getUserListing = () => {
  return (dispatch: Function, getState: Function) => {
    const { userType, page, limit, search, result: originalList } =
      getState().UserReducer;

    const queryParams: string[] = [];
    if (page) queryParams.push(`page=${page}`);
    if (limit) queryParams.push(`limit=${limit}`);
    if (search) queryParams.push(`search=${encodeURIComponent(search)}`);
    // USER_TYPE.COORDINATOR is 0, so an emptiness check is used instead of truthiness
    if (userType !== "" && userType !== null && userType !== undefined) {
      queryParams.push(`type=${userType}`);
    }

    dispatch({
      type: UPDATE_USER_REDUCER,
      payload: { loader: true },
    });

    GET_API_CALL(
      ENDPOINTS.USERS.LISTING,
      queryParams.length > 0 ? `?${queryParams.join("&")}` : "",
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          const dataToSave = mapPaginatedResult(response.data, mapUserListItem);
          if (page > 1) {
            dataToSave.result = [...originalList, ...dataToSave.result];
          }
          dispatch({
            type: UPDATE_USER_REDUCER,
            payload: { ...dataToSave, loader: false },
          });
        } else {
          dispatch({
            type: UPDATE_USER_REDUCER,
            payload: { loader: false },
          });
        }
      }
    );
  };
};

/**
 * POST /api/v1/admin/create-user
 */
export const AddNewUser = (values: any, callback: Function) => {
  return (dispatch: Function) => {
    const dataToSend: any = {
      role: values?.role,
      ...buildUserPayloadByRole(values?.role, values),
    };
    if (values?.password) dataToSend.password = values.password;

    dispatch({
      type: UPDATE_USER_REDUCER,
      payload: { loader: true },
    });

    POST_API_CALL(
      ENDPOINTS.USERS.CREATE_USER,
      dataToSend,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response?.data?.message, "success");
        }
        dispatch({
          type: UPDATE_USER_REDUCER,
          payload: { loader: false },
        });
        callback(response);
      }
    );
  };
};

/**
 * PATCH /api/v1/admin/update-user/{id} — payload varies by role.
 */
export const UpdateUser = (values: any, callback: Function) => {
  return (dispatch: Function, getState: Function) => {
    const userId = values?.id || getState().UserReducer?.UserId;
    const dataToSend: any = buildUserPayloadByRole(values?.role, values);
    // Blank password means keep the current one — only send when provided.
    const nextPassword = `${values?.password || ""}`.trim();
    const nextConfirmPassword = `${values?.confirmPassword || ""}`.trim();
    if (nextPassword) {
      dataToSend.password = nextPassword;
      dataToSend.confirmPassword = nextConfirmPassword;
    }

    dispatch({
      type: UPDATE_USER_REDUCER,
      payload: { loader: true },
    });

    PATCH_API_CALL(
      `${ENDPOINTS.USERS.UPDATE_USER}/${userId}`,
      dataToSend,
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          SHOW_TOAST(response?.data?.message, "success");
        }

        dispatch({
          type: UPDATE_USER_REDUCER,
          payload: { loader: false },
        });
        callback(response);
      }
    );
  };
};

/**
 * GET /api/v1/admin/user-details/{id}
 */
export const getUserDetail = () => {
  return (dispatch: Function, getState: Function) => {
    const { UserId } = getState().UserReducer;

    dispatch({
      type: UPDATE_USER_REDUCER,
      payload: { loader: true },
    });

    GET_API_CALL(
      `${ENDPOINTS.USERS.USER_DETAIL}/${UserId}`,
      "",
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          const userDetail = mapUserDetail(response.data);
          dispatch({
            type: UPDATE_USER_DETAIL_REDUCER,
            payload: { ...userDetail },
          });
        }
        dispatch({
          type: UPDATE_USER_DETAIL_REDUCER,
          payload: { loader: false },
        });
      }
    );
  };
};

export const getUserDetailFromEdit = (callback: any, userId?: string) => {
  return (dispatch: Function, getState: Function) => {
    const UserId = userId || getState().UserReducer.UserId;
    GET_API_CALL(
      `${ENDPOINTS.USERS.USER_DETAIL}/${UserId}`,
      "",
      (response: APIResponseModal) => {
        if (response.isSuccess) {
          const rawVehicle =
            (Array.isArray(response.data?.vehicle) && response.data.vehicle) ||
            (Array.isArray(response.data?.data?.vehicle) &&
              response.data.data.vehicle) ||
            [];
          const vehicles = rawVehicle.map((v: any) => ({
            _id: v?._id || v?.id || "",
            name: v?.name || "",
            companyName: v?.companyName || "",
            vehicleType: v?.vehicleType ?? "",
          }));
          const userDetail = mapUserDetail(response.data);
          callback({
            isSuccess: true,
            data: { ...userDetail, vehicle: vehicles },
          });
        } else {
          callback({ isSuccess: false, data: {} });
        }
        dispatch({
          type: UPDATE_USER_REDUCER,
          payload: { loader: false },
        });
      }
    );
  };
};

export const validateFile = async (file: File, validDimension: number) => {
  return new Promise((resolve, reject) => {
    if (
      file.type === "image/png" ||
      file.type === "image/jpg" ||
      file.type === "image/jpeg"
    ) {
      let img = new Image(),
        _URL = window.URL || window.webkitURL;
      var objectUrl = _URL.createObjectURL(file);
      img.onload = function () {
        const { width, height }: any = this,
          { size } = file;
        if (size / 1024 / 1024 < 1) {
          //converting to MB and if size is less than 1 mb then only proceed
          if (width <= validDimension && height <= validDimension) {
            //check if file dimensions are in check
            resolve({});
          } else {
            reject({
              message: `Image dimensions incorrect. Please upload an image that is less than ${validDimension}*${validDimension} pixels.`,
            });
          }
        } else {
          reject({
            message:
              "File size too large. Please upload a file that is no larger than 1 MB in size.",
          });
        }
        _URL.revokeObjectURL(objectUrl);
      };
      img.src = objectUrl;
    } else {
      reject({
        message: "Invalid file format. Please upload a JPG or PNG image.",
      });
    }
  });
};

export const getDashboardDataForUserDetails = (
  merchantId: string | null,
  startDate: string,
  endDate: string,
  callback: any
) => {
  return () => {
    GET_API_CALL(
      `${ENDPOINTS.DASHBOARD.DASHBOARD_DATA}?merchantId=${merchantId}&startDate=${startDate}&endDate=${endDate}`,
      "",
      (response: APIResponseModal) => {
        if (response.isSuccess && response.data) {
          const { result } = response.data;
          callback(result);
        } else {
          callback(null);
        }
      }
    );
  };
};
